import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Customer } from '../../types/customerTypes';
import { Invoice } from '../../types/invoiceTypes';
import Button from '../../components/common/Button/Button';
import { formatCurrency, formatDateDMY, formatDateTime, formatDateTimeDMY, drawStyledCompanyName } from '../../utils/helpers';
import invoiceService from '../../services/invoiceService';
import receivedAmountService from '../../services/receivedAmountService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import styles from './CustomerDetail.module.scss';
import customerService from '../../services/customerService';
import settingsService from '../../services/settingsService';
import { toast } from 'react-toastify';
import reportService, { BalanceReportDetail } from '../../services/reportService';

interface TransactionRow {
  id: string;
  type: 'invoice' | 'received';
  date: string;
  invoice_number?: string;
  amount: number;
  description?: string;
  status?: string;
}

const CustomerDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [receivedAmounts, setReceivedAmounts] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<TransactionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({ name: 'Invoice Management', address: '' });
  const [balanceDetail, setBalanceDetail] = useState<BalanceReportDetail | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const customerInfoRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    settingsService.getCompanyInfo().then(data => {
      setCompanyInfo({
        name: data.name,
        address: data.address,
      });
    });
  }, []);

  useEffect(() => {
    const loadCustomer = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const customer = await customerService.getCustomer(id);
        setCustomer(customer);
      } catch (error) {
        console.error('Failed to load customer:', error);
        toast.error('Failed to load customer');
        setCustomer(null);
      } finally {
        setLoading(false);
      }
    };

    loadCustomer();
  }, [id]);

  useEffect(() => {
    const loadInvoices = async () => {
      if (!id) return;

      setInvoicesLoading(true);
      try {
        const invoices = await invoiceService.getInvoicesByCustomer(id);
        setInvoices(Array.isArray(invoices) ? invoices : []);
      } catch (error) {
        console.error('Failed to load invoices:', error);
        toast.error('Failed to load customer invoices');
        setInvoices([]);
      } finally {
        setInvoicesLoading(false);
      }
    };

    loadInvoices();
  }, [id]);

  useEffect(() => {
    const loadReceivedAmounts = async () => {
      if (!id) return;

      try {
        const allReceivedAmounts = await receivedAmountService.getAll();
        const customerReceivedAmounts = allReceivedAmounts.filter(
          received => String(received.customer_id) === String(id)
        );
        setReceivedAmounts(customerReceivedAmounts);
      } catch (error) {
        console.error('Failed to load received amounts:', error);
        toast.error('Failed to load customer received amounts');
        setReceivedAmounts([]);
      }
    };

    loadReceivedAmounts();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setBalanceLoading(true);
    setBalanceError(null);
    reportService.getBalanceReportDetail(id)
      .then(data => setBalanceDetail(data))
      .catch(() => setBalanceError('Failed to load balance detail'))
      .finally(() => setBalanceLoading(false));
  }, [id]);

  // Combine invoices and received amounts into transactions
  useEffect(() => {
    const combinedTransactions: TransactionRow[] = [];

    // Add invoices with unique identifiers
    invoices.forEach((invoice, index) => {
      combinedTransactions.push({
        id: `invoice-${invoice.id}-${index}`,
        type: 'invoice',
        date: invoice.created_at,
        invoice_number: invoice.invoice_number,
        amount: Number(invoice.total_amount) || 0,
        description: invoice.invoice_number || 'Invoice',
        status: invoice.status
      });
    });

    // Add received amounts with unique identifiers and better descriptions
    receivedAmounts.forEach((received, index) => {
      combinedTransactions.push({
        id: `received-${received.id}-${index}`,
        type: 'received',
        date: received.created_at,
        amount: Number(received.amount) || 0,
        description: received.notes || received.description
      });
    });

    // Sort by date (newest first), then by ID to maintain consistent ordering
    combinedTransactions.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      
      if (dateA === dateB) {
        // If dates are same, sort by ID to maintain consistent order
        return a.id.localeCompare(b.id);
      }
      
      return dateB - dateA;
    });

    setTransactions(combinedTransactions);
  }, [invoices, receivedAmounts]);

  const generateStructuredPDF = async () => {
    if (!customer) return;

    setIsGeneratingPDF(true);

    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const pageHeight = pdf.internal.pageSize.height;
      let yPos = 20;

      // Company Header with styled first letter
      const companyName = companyInfo?.name && companyInfo.name.length > 0 ? companyInfo.name : 'Company Name';
      drawStyledCompanyName(pdf, companyName, yPos, pageWidth);
      
      yPos += 8;
      const companyAddress = companyInfo?.address || 'Company Address';
      pdf.text(companyAddress, pageWidth / 2, yPos, { align: 'center' });

      yPos += 8;
      const currentDate = new Date();
      pdf.text(`Printed on ${currentDate.toLocaleDateString()} - ${currentDate.toLocaleTimeString()}`, pageWidth / 2, yPos, { align: 'center' });

      yPos += 15;

      // Customer Header Section
      //pdf.setFillColor(128, 128, 128); // Gray background
      //pdf.rect(14, yPos, pageWidth - 28, 12, 'F');
      
      pdf.setTextColor(255, 255, 255); // White text
      pdf.setFontSize(12);
      pdf.text(customer.name.toUpperCase(), 16, yPos + 8);

      yPos += 20;

      // Calculate totals consistently
      const totalInvoiceAmount = invoices.reduce((sum, inv) => sum + (Number(inv.total_amount) || 0), 0);
      const totalReceivedAmount = receivedAmounts.reduce((sum, received) => sum + (Number(received.amount) || 0), 0);
      const totalOutstanding = totalInvoiceAmount - totalReceivedAmount;

      // Total Outstanding
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(10);
      pdf.text('Total Outstanding :', 20, yPos);

      pdf.text(totalOutstanding.toFixed(2), pageWidth - 20, yPos, { align: 'right' });

      yPos += 15;

      // Table headers
      const headers = ['S.No', 'Date/Time', 'Account Head', 'Description', 'Invoice Amount', 'Received Amount'];
      const columnWidths = [10, 30, 30, 50, 30, 30];
      const startX = 14;
      
      // Draw table header
      pdf.setFillColor(128, 128, 128);
      pdf.rect(startX, yPos, pageWidth - 28, 10, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      
      let xPos = startX + 2;
      headers.forEach((header, index) => {
        pdf.text(header, xPos, yPos + 7);
        xPos += columnWidths[index];
      });

      yPos += 12;

      // Table data with improved unique descriptions
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      
      let serialNo = 1;
      transactions.forEach((transaction, index) => {
        // Check if we need a new page
        if (yPos > pageHeight - 40) {
          pdf.addPage();
          yPos = 20;
        }

        // Create better descriptions to avoid merging appearance
        let description = '';
        if (transaction.type === 'invoice') {
          description = transaction.description || transaction.invoice_number || 'Invoice';
        } else {
          // For payments, include timestamp or unique identifier if available
          const paymentDesc = transaction.description || 'Payment received';
          description = paymentDesc === 'Payment received' ? `${paymentDesc}` : paymentDesc;
        }

        const row = [
          serialNo++,
          formatDateTimeDMY(transaction.date),
          transaction.type === 'invoice' ? (transaction.invoice_number?.includes('/') ? transaction.invoice_number.split('/')[0] : 'MIS') : '',
          description,
          transaction.type === 'invoice' ? transaction.amount.toFixed(2) : '0',
          transaction.type === 'received' ? transaction.amount.toFixed(2) : '0'
        ];

        // Draw row background (alternate colors)
        if (index % 2 === 1) {
          pdf.setFillColor(245, 245, 245);
          pdf.rect(startX, yPos, pageWidth - 28, 8, 'F');
        }

        // Draw row content
        xPos = startX + 2;
        row.forEach((cell, cellIndex) => {
          pdf.text(cell.toString(), xPos, yPos + 6);
          xPos += columnWidths[cellIndex];
        });

        yPos += 10;

        if (transaction.type === 'invoice' && Array.isArray(invoices) && invoices.length > 0) {
          const invoice = invoices.find(inv => String(inv.invoice_number) === String(transaction.invoice_number));
          if (invoice && Array.isArray(invoice.items) && invoice.items.length > 0) {
            // Add item table header
            pdf.setFontSize(7);
            pdf.setTextColor(60, 60, 60);
            pdf.text('Item Description', startX + 8, yPos + 6);
            pdf.text('Item Total Amount', startX + 80, yPos + 6);
            yPos += 8;
            // Add each item row
            invoice.items.forEach((item) => {
              if (yPos > pageHeight - 40) {
                pdf.addPage();
                yPos = 20;
              }
              pdf.setFontSize(7);
              pdf.setTextColor(80, 80, 80);
              pdf.text('  • ' + (item.description || '-'), startX + 8, yPos + 6);
              pdf.text(((Number(item.amount) || 0) + (Number(item.service_charge) || 0)).toFixed(2), startX + 80, yPos + 6);
              yPos += 8;
            });
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0);
          }
        }
      });

      // Add total row to the table
      if (transactions.length > 0) {
        // Check if we need a new page for the total row
        if (yPos > pageHeight - 40) {
          pdf.addPage();
          yPos = 20;
        }

        // Draw total row background
        pdf.setFillColor(128, 128, 128);
        pdf.rect(startX, yPos, pageWidth - 28, 10, 'F');
        
        // Total row content
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        
        const totalRow = [
          'Total',
          '',
          '',
          '',
          totalInvoiceAmount.toFixed(2),
          totalReceivedAmount.toFixed(2)
        ];

        xPos = startX + 2;
        totalRow.forEach((cell, cellIndex) => {
          if (cellIndex === 0) {
            // "Total" text
            pdf.text(cell, xPos, yPos + 7);
          } else if (cellIndex === 4 || cellIndex === 5) {
            // Amount columns - right aligned with more space
            const cellWidth = columnWidths[cellIndex];
            pdf.text(cell, xPos + cellWidth - 20, yPos + 7, { align: 'right' });
          }
          xPos += columnWidths[cellIndex];
        });

        yPos += 12;
      }

      // Save the PDF
      pdf.save(`customer-${customer.name.replace(/\s+/g, '-').toLowerCase()}-statement.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const filteredTransactions = transactions.filter((transaction) => {
    const search = searchTerm.toLowerCase();
  
    // Match description in transaction
    const matchesTransactionDesc = transaction.description?.toLowerCase().includes(search);
  
    // If invoice → check items too
    let matchesItemDesc = false;
    if (transaction.type === "invoice") {
      const invoice = invoices.find((inv) => String(inv.invoice_number) === String(transaction.invoice_number));
      if (invoice && Array.isArray(invoice.items)) {
        matchesItemDesc = invoice.items.some((item) =>
          item.description?.toLowerCase().includes(search)
        );
      }
    }
  
    return matchesTransactionDesc || matchesItemDesc;
  });

  const downloadPdfFromContent = async () => {
    if (!contentRef.current || !customer) return;
    try {
      setIsGeneratingPDF(true);
      const element = contentRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: document.documentElement.scrollWidth,
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pdfWidth;
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`customer-${customer.name.replace(/\s+/g, '-').toLowerCase()}-statement.pdf`);
    } catch (error) {
      console.error('Error generating PDF from content:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const getCustomerPrintContent = () => {
    if (!customer) return '';
    const now = new Date();
    const printDate = now.toLocaleDateString();
    const printTime = now.toLocaleTimeString();
    const companyName = companyInfo?.name && companyInfo.name.length > 0 ? companyInfo.name : 'Company Name';
    const companyAddress = companyInfo?.address || 'Company Address';
    const firstLetter = companyName.charAt(0);
    const restOfName = companyName.slice(1);

    const totalInvoiceAmountLocal = invoices.reduce((sum, inv) => sum + (Number(inv.total_amount) || 0), 0);
    const totalReceivedAmountLocal = receivedAmounts.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const totalOutstandingLocal = totalInvoiceAmountLocal - totalReceivedAmountLocal;

    return `
      <div style="text-align:center;">
        <h1 style="margin-bottom:0;">
          <span style="color:red; font-size:2rem; font-weight:bold;">${firstLetter}</span>
          <span style="color:green; font-size:1.4rem; font-weight:bold; margin-left:-8px;">${restOfName}</span>
        </h1>
        <div style="font-size:1rem; margin-bottom:6px;">${companyAddress}</div>
        <div style="margin-bottom:6px;">Printed on ${printDate} - ${printTime}</div>
      </div>
       <div style="background:none !important; color:black !important; border:1px solid black; padding:8px; font-size:1.1rem; margin-bottom:8px; -webkit-print-color-adjust: exact; color-adjust: exact; print-color-adjust: exact;">
            Total Outstanding: ${totalOutstandingLocal.toLocaleString()}
          </div>
      <div style="display:flex; justify-content:space-between; font-weight:600; margin-bottom:8px;">
        <div>Customer: ${customer.name}</div>
        <div>Status: ${customer.is_active ? 'Active' : 'Inactive'}</div>
      </div>
      <div style="margin-bottom:8px;">
        <div><strong>Email:</strong> ${customer.email || '-'}</div>
        <div><strong>Phone:</strong> ${customer.phone || '-'}</div>
        <div><strong>Address:</strong> ${customer.address || '-'}</div>
      </div>
      <table style="width:100%; border-collapse:collapse; margin-bottom:8px;">
        <thead>
          <tr>
            <th style="border:1px solid #ccc; padding:6px;">S.No</th>
            <th style="border:1px solid #ccc; padding:6px;">Date/Time</th>
            <th style="border:1px solid #ccc; padding:6px;">Account Head</th>
            <th style="border:1px solid #ccc; padding:6px;">Description</th>
            <th style="border:1px solid #ccc; padding:6px;">Invoice Amount</th>
            <th style="border:1px solid #ccc; padding:6px;">Received Amount</th>
          </tr>
        </thead>
        <tbody>
          ${transactions.map((t, idx) => {
            const invoiceAmt = t.type === 'invoice' ? (Number(t.amount) || 0) : 0;
            const receivedAmt = t.type === 'received' ? (Number(t.amount) || 0) : 0;
            const description =
    t.type === 'invoice'
      ? `${t.description || '-'} - Total`
      : t.description || '-';

  // Invoice amount with bold if invoice
  const invoiceCell =
    t.type === 'invoice'
      ? `<strong>${invoiceAmt.toLocaleString()}</strong>`
      : invoiceAmt.toLocaleString();

      const baseRow = `
      <tr style="${t.type === 'invoice' ? 'background:#f0f8ff !important; color:#000 !important; -webkit-print-color-adjust: exact; color-adjust: exact; print-color-adjust: exact;' : ''}">
        <td style="border:1px solid #ccc; padding:6px;">${idx + 1}</td>
        <td style="border:1px solid #ccc; padding:6px;">${formatDateDMY(t.date)}</td>
        <td style="border:1px solid #ccc; padding:6px;"></td>
        <td style="border:1px solid #ccc; padding:6px;">${description}</td>
        <td style="border:1px solid #ccc; padding:6px;">${invoiceCell}</td>
        <td style="border:1px solid #ccc; padding:6px;">${receivedAmt.toLocaleString()}</td>
      </tr>
    `;
            
            // Add item details for invoices
            if (t.type === 'invoice' && Array.isArray(invoices) && invoices.length > 0) {
              const invoice = invoices.find(inv => String(inv.invoice_number) === String(t.invoice_number));
              if (invoice && Array.isArray(invoice.items) && invoice.items.length > 0) {
                const itemRows = invoice.items.map((item, itemIdx) => {
                  // Check for account head name - it should be account_head_name based on the backend query
                  const accountHeadName = item.account_head_name || 'N/A';
                  
                  return `
                    <tr style="background:#f9fafb;">
                      <td style="border:1px solid #ccc; padding:6px;"></td>
                      <td style="border:1px solid #ccc; padding:6px;"></td>
                      <td style="border:1px solid #ccc; padding:6px;">${accountHeadName}</td>
                      <td style="border:1px solid #ccc; padding:6px; padding-left:20px;">• ${item.description || '-'}</td>
                      <td style="border:1px solid #ccc; padding:6px;">${((Number(item.amount) || 0) + (Number(item.service_charge) || 0)).toLocaleString()}</td>
                      <td style="border:1px solid #ccc; padding:6px;"></td>
                    </tr>
                  `;
                }).join('');
                return baseRow + itemRows;
              }
            }
            return baseRow;
          }).join('')}
          ${transactions.length > 0 ? `
            <tr style="font-weight:bold; background:#f5f5f5;">
              <td colspan="4" style="text-align:right; border:1px solid #ccc; padding:6px;">Total</td>
              <td style="border:1px solid #ccc; padding:6px;">${totalInvoiceAmountLocal.toLocaleString()}</td>
              <td style="border:1px solid #ccc; padding:6px;">${totalReceivedAmountLocal.toLocaleString()}</td>
            </tr>
          ` : ''}
        </tbody>
      </table>
    `;
  };

  const handleCustomerPrint = () => {
    const printWindow = window.open('', '', 'height=800,width=1100');
    if (printWindow) {
      printWindow.document.write('<html><head><title>Customer Statement Print</title>');
      printWindow.document.write(`
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ccc; padding: 8px; }
          th { background: #f5f5f5; }
        </style>
      `);
      printWindow.document.write('</head><body>');
      printWindow.document.write(getCustomerPrintContent());
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleDownloadPDF = () => {
    // Use html2canvas-based export to preserve Unicode (e.g., Tamil) characters
    downloadPdfFromContent();
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading customer...</p>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className={styles.errorContainer}>
        <h2>Customer Not Found</h2>
        <p>The customer you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/customers')}>
          Back to Customers
        </Button>
      </div>
    );
  }

  // FIX: Use consistent calculation with receivedAmounts instead of partially_received_amounts
  const totalInvoiceAmount = invoices.reduce((sum, inv) => sum + (Number(inv.total_amount) || 0), 0);
  const totalReceivedAmount = receivedAmounts.reduce((sum, received) => sum + (Number(received.amount) || 0), 0);
  const totalBalanceAmount = totalInvoiceAmount - totalReceivedAmount;

  // Calculate overall report items total amount (sum of all invoice items' amount + service_charge)
  const itemsTotalAmount = invoices.reduce((sum, inv) => {
    const amount = Array.isArray(inv.items)
      ? inv.items.reduce((itemSum, item) => itemSum + (Number(item.amount) || 0) + (Number(item.service_charge) || 0), 0)
      : 0;
    return sum + amount;
  }, 0);

  return (
    <div className={styles.customerDetail} ref={contentRef}>
      {/* Print-only header for better PDF formatting */}
      <div className={styles.printHeader}>
        <h2>{customer.name}</h2>
        <p>Status: {customer.is_active ? 'Active' : 'Inactive'}</p>
      </div>

      <div className={styles.header}>
        <div className={styles.titleSection}>
          <button
            className={styles.collapseToggle}
            onClick={() => {
              navigate(-1);
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          
          <h2>{customer.name}</h2>
          <div className={styles.meta}>
            <span className={`${styles.status} ${customer.is_active ? styles.active : styles.inactive}`}>
              {customer.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        <div className={styles.actions}>
          <Button
            variant="secondary"
            onClick={handleCustomerPrint}
            disabled={isGeneratingPDF}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print
          </Button>
          {/* <Button
            variant="primary"
            onClick={handleDownloadPDF}
            disabled={isGeneratingPDF}
            loading={isGeneratingPDF}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
              <path d="M21,15v4a2,2 0 0,1-2,2H5a2,2 0 0,1-2-2v-4"></path>
              <polyline points="7,10 12,15 17,10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Download PDF
          </Button> */}
        </div>
      </div>

      <div className={styles.content}>
        <div ref={customerInfoRef}>
          <div className={styles.section}>
            <h3>Contact Information</h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>Email Address</label>
                <span>{customer.email}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Phone Number</label>
                <span>{customer.phone}</span>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h3>Address Information</h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>Street Address</label>
                <span>{customer.address}</span>
              </div>
              <div className={styles.infoItem}>
                <label>City</label>
                <span>{customer.city}</span>
              </div>
              <div className={styles.infoItem}>
                <label>State/Province</label>
                <span>{customer.state}</span>
              </div>
              <div className={styles.infoItem}>
                <label>ZIP/Postal Code</label>
                <span>{customer.zip_code}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Country</label>
                <span>{customer.country}</span>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <h3>Summary</h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <label>Total Invoice Amount</label>
                <span>{formatCurrency(totalInvoiceAmount)}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Total Received Amount</label>
                <span>{formatCurrency(totalReceivedAmount)}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Balance Amount</label>
                <span>{formatCurrency(totalBalanceAmount)}</span>
              </div>
              {/* <div className={styles.infoItem}>
                <label>Report Items Total Amount</label>
                <span>{formatCurrency(itemsTotalAmount)}</span>
              </div> */}
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h3>Transaction History</h3>
          <div style={{ marginBottom: "12px" }}>
    <input
      type="text"
      placeholder="Search by description or item..."
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      style={{
        padding: "8px",
        border: "1px solid #ccc",
        borderRadius: "6px",
        width: "100%",
        maxWidth: "300px",
      }}
    />
  </div>
          {invoicesLoading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No transactions found for this customer.</p>
            </div>
          ) : (
            <div className={styles.invoiceTable}>
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Invoice #</th>
                    <th>Description</th>
                    <th>Amount</th>
                    {/* <th>Status</th> */}
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((transaction) => (
                    <React.Fragment key={transaction.id}>
                      <tr className={transaction.type === 'received' ? styles.receivedRow : styles.invoiceRow}>
                        <td>
                          <span className={`${styles.typeBadge} ${styles[transaction.type]}`}>
                            {transaction.type === 'invoice' ? 'Invoice' : 'Payment'}
                          </span>
                        </td>
                        <td>{formatDateTimeDMY(transaction.date)}</td>
                        <td>
                          {transaction.type === 'invoice' && transaction.invoice_number ? (
                            <span
                              onClick={() => {
                                const invoice = invoices.find(inv => String(inv.invoice_number) === String(transaction.invoice_number));
                                if (invoice) {
                                  navigate(`/invoices/${invoice.id}`);
                                }
                              }}
                              style={{ color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
                            >
                              {transaction.invoice_number}
                            </span>
                          ) : (
                            transaction.invoice_number || '-'
                          )}
                        </td>
                        <td>{transaction.description || '-'}</td>
                        <td className={transaction.type === 'received' ? styles.receivedAmount : styles.invoiceAmount}>
                          {transaction.type === 'received' ? '+' : ''}{formatCurrency(transaction.amount)}
                        </td>
                        {/* <td>
                          {transaction.status && (
                            <span className={`${styles.status} ${styles[transaction.status]}`}>
                              {transaction.status.replace('_', ' ').toUpperCase()}
                            </span>
                          )}
                        </td> */}
                      </tr>
                      {/* Show invoice items if this is an invoice and items are available */}
                      {transaction.type === 'invoice' && Array.isArray(invoices) && invoices.length > 0 && (() => {
                        const invoice = invoices.find(inv => String(inv.invoice_number) === String(transaction.invoice_number));
                        if (invoice && Array.isArray(invoice.items) && invoice.items.length > 0) {
                          return (
                            <tr>
                              <td colSpan={6} style={{ padding: 0, background: '#f9fafb' }}>
                                <table style={{ width: '100%', margin: 0, background: '#f9fafb' }}>
                                  <thead>
                                    <tr>
                                      <th style={{ fontWeight: 500 }}>Item Description</th>
                                      <th style={{ fontWeight: 500 }}>Item Total Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {invoice.items.map((item, idx) => (
                                      <tr key={idx}>
                                        <td>{item.description || '-'}</td>
                                        <td>{formatCurrency((Number(item.amount) || 0) + (Number(item.service_charge) || 0))}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          );
                        }
                        return null;
                      })()}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;
